<script lang="ts">
  /**
   * The sales page.
   *
   * THE DESIGN IDEA, in one line: rowdy copy, calm instrument. The words on this page
   * shout; everything around them is the same flat, hairlined, mono-labelled panel the
   * product itself is made of. The contrast is the point — the promise is "we turn your
   * chaos into a readout", so the page had better look like a readout.
   *
   * THREE THINGS CARRY IT, and nothing else is allowed to compete:
   *
   *  1. THE SPINE. Every act sits on a two-column grid: a mono marker in a left gutter,
   *     the copy in a ~38rem measure beside it. A long sales page that centres every
   *     block reads as a stack of unrelated pitches; a left rail with running labels
   *     reads as one document, and it is the same left-rail vocabulary as the admin
   *     panel. The gutter collapses under `lg` — on a phone the marker just sits above
   *     its section.
   *  2. THE LEAK (`#leak`). The one memorable object, in the one section that argues
   *     rather than describes. See its own comment.
   *  3. THE TYPE. Bricolage at 800 with tight tracking for the shouting, IBM Plex Sans
   *     for the one-line paragraphs the copy is built from, and `.eyebrow` — the mono
   *     readout label `app.css` calls "the instrument texture used throughout" — for
   *     every marker and stat. No fourth voice.
   *
   * WHAT WAS DELIBERATELY NOT BUILT: scroll-reveal on each section (the page is long,
   * and a dozen fade-ups is how a page announces that it was generated rather than
   * designed), and the early-bird slot counter the previous version had. The offer copy
   * now sells "locked in forever" rather than scarcity, and a progress bar that nobody
   * asked for is the accessory to take off before leaving the house.
   *
   * Motion lives in exactly one place, `#leak`, and stops under `prefers-reduced-motion`.
   */
  import { navigate } from '../lib/router.svelte';
  import { captureVia } from '../lib/affiliates';
  import { getToken } from '../lib/admin';

  // Affiliate attribution: if someone lands via `/?via=CODE`, remember the code
  // (last-touch) so a later signup gets credited. Runs once on mount; leaves the
  // URL untouched and shows no UI.
  captureVia();

  // Two plans: Free for communities under FREE_MEMBER_CAP members, Pro for everyone
  // else. Early birds lock the Pro price in forever.
  const FREE_MEMBER_CAP = 100;
  const PRO_PRICE = 287;
  const EARLY_PRICE = 87;

  // Real Skool communities running on Sideguide — logos live in public/media.
  // Every community with a Sideguide account behind it, largest first. Two deliberate
  // omissions: the communities held by our OWN accounts (Hybrid Hoops), which would be
  // claiming ourselves as a customer, and searchdogmarketing2010's two. Vibe Consultant
  // Pro is the one entry with no account of its own — it is Andrew's, ingested for the
  // testimonial below. Logos are the real Skool assets, pulled from `skools.logo_url`.
  // Hand-maintained: a new customer does not appear here until someone adds a row.
  // `url` is the community's own Skool page, from `skools.slug` — the same two-link proof
  // the testimonial cards make: the claim is checkable by clicking it. nofollow, like
  // those, so a dozen outbound links off the landing page carry no SEO weight.
  //
  // `members` is the EXACT headcount from `skools.total_members`, snapshotted 2026-08-14.
  // It is stored exact and rounded down at render (`floor10`) rather than stored
  // pre-rounded: the number here stays checkable against the database, and a floor is
  // the one form of rounding that cannot overstate a community — "670+" is still true
  // months later as it grows, where "673" is wrong the day after it ships.
  const proof = [
    { name: 'Productivity Skool', logo: '/media/communities/productivityskool.jpg', url: 'https://www.skool.com/productivityskool', members: 673 },
    { name: 'One Call Close', logo: '/media/communities/onecall.jpg', url: 'https://www.skool.com/onecall', members: 322 },
    { name: 'Authentic AF', logo: '/media/communities/authenticaf.jpg', url: 'https://www.skool.com/authenticaf', members: 311 },
    { name: 'Fighter Freedom Accelerator', logo: '/media/communities/size-and-violence.jpg', url: 'https://www.skool.com/size-and-violence-8786', members: 280 },
    { name: 'Skool Dads', logo: '/media/communities/skool-dads.jpg', url: 'https://www.skool.com/skool-dads', members: 185 },
    { name: 'Dadland', logo: '/media/communities/dadland.jpg', url: 'https://www.skool.com/dadland', members: 98 },
    { name: 'Vibe Consultant Pro', logo: '/media/communities/vibe-consultant-pro.jpg', url: 'https://www.skool.com/vibe-consultant-pro', members: 84 },
    { name: 'Be A Pro Network', logo: '/media/communities/be-a-pro-network.jpg', url: 'https://www.skool.com/be-a-pro-network-3492', members: 35 },
    { name: 'Skool Nerds', logo: '/media/communities/skool-nerds.jpg', url: 'https://www.skool.com/skool-nerds-4383', members: 34 },
    { name: 'The Honest Debrief', logo: '/media/communities/calm-connected.jpg', url: 'https://www.skool.com/calm-connected-9177', members: 33 },
    { name: 'Project Controls AI', logo: '/media/communities/engage-more-for-you-llc.jpg', url: 'https://www.skool.com/engage-more-for-you-llc-8477', members: 25 },
    { name: 'Memberflow', logo: '/media/communities/memberflow.jpg', url: 'https://www.skool.com/memberflow', members: 22 },
  ];

  /** Headcount floored to the nearest 10, as "90+" / "310+". Floor, never nearest: a
   *  community must never be shown as larger than it is. */
  const floor10 = (n: number): string => `${Math.floor(n / 10) * 10}+`;

  /** The range the strip claims, computed from the rows rather than typed into the
   *  headline — so "20 to 670+" cannot drift out of date while the table below it does. */
  const smallest = floor10(Math.min(...proof.map((c) => c.members)));
  const largest = floor10(Math.max(...proof.map((c) => c.members)));

  /**
   * The owner reviews, as data.
   *
   * One record per review and ONE card that renders them, because the previous version
   * carried four hand-copied ~60-line blocks that differed only in a filename and a
   * duration — which is how the third one ends up with the second one's aria-label.
   * `playing` is per-card state held in a `$state` map keyed by id.
   *
   * `stat` is the line under the community: whatever is the strongest checkable fact
   * about that owner. It is deliberately not the same KIND of fact for each — inventing
   * a member count for an operator who runs other people's communities would be worse
   * than saying what they actually do.
   */
  const reviews = [
    {
      id: 'andrew', name: 'Andrew Dunn', community: 'Vibe Consultant Pro', stat: '$460,000 ARR',
      profile: 'https://www.skool.com/@andrew-dunn-9386', duration: '1:49',
    },
    {
      id: 'brian', name: 'Brian Walsh', community: 'Memberflow Operators',
      stat: 'Skool operator for high-profile creators',
      profile: 'https://www.skool.com/@brianwalsh', duration: '1:25',
    },
    {
      id: 'andy', name: 'Andy Grant', community: 'Authentic AF', stat: '300+ members',
      profile: 'https://www.skool.com/@theandygrant', duration: '1:20',
    },
  ];

  // Click-to-play facade: the page ships only a lazy poster, and the mp4 downloads when
  // somebody actually hits play. Three videos autoplaying on load would cost ~27MB.
  let playing = $state<Record<string, boolean>>({});

  /**
   * The five leaks, each labelled with the part of the product that plugs it.
   *
   * THE LABEL IS THE STRUCTURE, and it replaced `01 / 02 / 03`. These five are a list,
   * not a sequence — nothing about "catch quiet members" comes before "stop letting
   * questions die" — so an index would have been decoration pretending to be order.
   * The instrument name is the fact worth encoding: it tells a reader which part of the
   * panel does this, and it is the same vocabulary as the Pro feature list further down,
   * so the two sections visibly describe one product.
   */
  const leaks = [
    {
      label: 'Momentum & churn',
      title: 'Catch quiet members BEFORE they’re gone.',
      body: 'See momentum and churn signals that help you identify members drifting away before their cancellation becomes the first time you notice.',
      punch: 'Because “Your subscription has been cancelled” is a pretty shitty churn alert.',
    },
    {
      label: 'Pipelines',
      title: 'Know exactly who needs attention.',
      body: 'Stop digging through profiles, comments and activity. Use member pipelines and reactivation queues to turn a giant member list into a clear list of people worth reaching out to.',
      punch: 'Less hunting. More helping.',
    },
    {
      label: 'Questions & gaps',
      title: 'Stop letting good questions die.',
      body: 'Surface unanswered questions, content gaps and moderation work in one place. No more discovering three days later that somebody asked a brilliant question…',
      punch: 'And heard crickets.',
    },
    {
      label: 'Participation',
      title: 'See whether your “engaged community” is actually engaged.',
      body: 'Likes can make everything look peachy. Sideguide helps you look underneath them. See participation patterns, concentration and retention cohorts so you can understand whether the whole community is moving…',
      punch: 'Or six power users are making the place look alive.',
    },
    {
      label: 'AI tutor',
      title: 'Give members answers without answering everything yourself.',
      body: 'Turn your existing community knowledge into a 24/7 AI tutor that helps members find answers and points them back to the original material. Your members get help. Your content gets used.',
      punch: 'You don’t answer the same bloody question for the 47th time.',
    },
  ];

  /** The member reviews — text, from members of a customer's community rather than its
   *  owner. Same one-implementation rule as `reviews`. */
  const memberQuotes = [
    {
      id: 'peyton', name: 'Peyton Hoang', profile: 'https://www.skool.com/@peyton-hoang-4080',
      quote: 'Instead of spamming my mentor 10 times a day, I can just ask the AI — and having it reference exactly where the information’s coming from has been extremely helpful.',
    },
    {
      id: 'erica', name: 'Erica Byrd-Ekhibise', profile: 'https://www.skool.com/@erica-byrd-ekhibise-8104',
      quote: 'No beating around the bush or searching through post after post. Sideguide helps me get direct answers to my questions.',
    },
    {
      id: 'sohaib', name: 'Sohaib Shahid', profile: 'https://www.skool.com/@sohaib-shahid-3559',
      quote: 'It feels like you’re having a conversation with the creator of the community… every response includes references to the original content.',
    },
  ];

  /** The four stages of `#leak`. `gap` marks the three joins members fall through —
   *  the last stage has nowhere further to leak TO, so it carries no drip. */
  const funnel = [
    { key: 'get', label: 'Get', note: 'They join' },
    { key: 'activate', label: 'Activate', note: 'They post' },
    { key: 'engage', label: 'Engage', note: 'They stay active' },
    { key: 'keep', label: 'Keep', note: 'They stay paid' },
  ];

  function getStarted(): void {
    navigate('/register');
  }

  // Read ONCE, not reactively: nothing on this page can sign you in or out, so the
  // answer cannot change while it is open.
  const signedIn = getToken() !== null;
</script>

<!-- ── Nav ──────────────────────────────────────────────────────────────────── -->
<header class="mx-auto flex max-w-5xl items-center gap-4 px-6 py-5">
  <a href="/" class="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
    <img src="/icon-128.png" alt="" class="h-7 w-7 rounded-md" />
    Sideguide
  </a>
  <!-- ONE action, whichever one the visitor can actually take: the dashboard if they
       already have a session, otherwise the same ask the hero makes — deliberately the
       identical label and the identical `getStarted`, so the page makes one ask rather
       than two differently-worded ones. -->
  <div class="ml-auto">
    {#if signedIn}
      <button class="btn btn-primary btn-sm" onclick={() => navigate('/admin')}>Dashboard</button>
    {:else}
      <button class="btn btn-primary btn-sm" onclick={getStarted}>Get Sideguide free</button>
    {/if}
  </div>
</header>

<!-- ── Hero ─────────────────────────────────────────────────────────────────── -->
<section class="hero-glow border-b hair">
  <!-- The hero sits on the SAME two-column spine as every act below it — marker in the
       gutter, everything else in the measure. It first shipped flush to the container,
       which put the headline 172px left of every section heading under it and read as a
       misalignment rather than as a deliberate break. -->
  <div class="mx-auto max-w-5xl px-6 pb-16 pt-12 sm:pt-20 lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
    <p class="eyebrow mb-5 text-base-content/55 lg:mb-0 lg:pt-4">For Skool community owners</p>
    <div>
    <!-- The headline is the loudest thing on the page and nothing else is allowed to
         compete with it: 800 weight, tracking pulled in hard, and a size that only
         works because the line is four words long. -->
    <h1 class="mt-5 max-w-[20ch] font-display text-[2.75rem] font-extrabold leading-[0.95] tracking-[-0.03em] sm:text-[4.5rem]">
      Stop working<br />for your Skool.
    </h1>

    <p class="mt-7 max-w-[42rem] text-lg leading-relaxed text-base-content/75 sm:text-xl">
      Let Sideguide find the members going quiet, surface what needs your attention and
      handle the repetitive crap — so you can get back to growing the damn thing.
    </p>

    <!-- The copy's own rhythm: three one-line beats, set as a stack rather than a
         paragraph so each one lands separately. The rule on the left is the same
         hairline the panel uses to group a readout. -->
    <div class="mt-9 border-l-2 border-primary/25 pl-5 text-base leading-relaxed">
      <p class="text-base-content/70">Your Skool grew.</p>
      <p class="text-base-content/70">Unfortunately…</p>
      <p class="mt-1 font-display text-xl font-bold tracking-tight">So did the workload.</p>
    </div>

    <p class="mt-7 max-w-[42rem] text-base leading-relaxed text-base-content/75">
      Sideguide turns the chaos into one simple command center for growing, engaging and
      retaining your members.
    </p>

    <div class="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
      <button class="btn btn-primary btn-lg gap-2" onclick={getStarted}>
        Get Sideguide free
        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
      <p class="eyebrow text-base-content/60">
        Free under {FREE_MEMBER_CAP} members · Set up in minutes · No card required
      </p>
    </div>
    </div>
  </div>
</section>

<!-- ── Proof strip ──────────────────────────────────────────────────────────── -->
<section class="border-b hair bg-base-200/40">
  <div class="mx-auto max-w-5xl px-6 py-10 lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
    <p class="eyebrow mb-6 text-base-content/60 lg:mb-0 lg:pt-1">
      Trusted by<br class="hidden lg:block" /> {smallest.replace('+', '')}–{largest} members
    </p>
    <div class="flex flex-wrap items-center gap-x-7 gap-y-4">
      {#each proof as c (c.name)}
        <a href={c.url} target="_blank" rel="nofollow noopener" class="group flex items-center gap-2.5">
          <img src={c.logo} alt="" loading="lazy" class="h-8 w-8 rounded-lg object-cover" />
          <span class="flex flex-col leading-tight">
            <!-- Only the NAME underlines on hover; the headcount under it is a fact about
                 the community, not part of the link's label. -->
            <!-- privacy-ok: the public marketing page — no customer data on it -->
            <span class="font-display text-[0.9rem] font-semibold tracking-tight text-base-content/80 group-hover:underline">{c.name}</span>
            <span class="font-mono text-[0.65rem] text-base-content/50">{floor10(c.members)} members</span>
          </span>
        </a>
      {/each}
    </div>
  </div>
</section>

<!-- ── The problem ──────────────────────────────────────────────────────────── -->
<section class="border-b hair">
  <div class="mx-auto max-w-5xl px-6 py-20 lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
    <p class="eyebrow mb-6 text-base-content/55 lg:mb-0 lg:pt-3">The problem</p>
    <div class="max-w-[38rem]">
      <h2 class="font-display text-3xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-[2.75rem]">
        Congrats. Your Skool is growing.
      </h2>
      <p class="mt-4 font-display text-xl font-bold tracking-tight text-base-content/60 sm:text-2xl">
        You’ve also accidentally created yourself another job.
      </p>

      <div class="mt-9 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>Remember when you had 20 members?</p>
        <p>You knew everyone.</p>
        <p>You noticed when somebody disappeared.</p>
        <p>You could reply to every question.</p>
        <p>Welcome every new member.</p>
        <p>Jump into the comments.</p>
        <p>Keep the place buzzing.</p>
        <p class="font-medium text-base-content">Easy peasy.</p>
      </div>

      <div class="mt-7 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>Then you grew.</p>
        <p>50 members became 100.</p>
        <p>100 became 300.</p>
        <p>
          And suddenly your cute little recurring-revenue machine started demanding more
          attention than a Labrador puppy with an espresso addiction.
        </p>
      </div>

      <p class="mt-9 text-base text-base-content/75">Now there are…</p>

      <!-- THE PILE. The copy lists six unfinished things; rendering them as prose would
           bury the one property that matters — that there are six of them and they keep
           coming. So they are a hairlined stack of unread rows, the same shape as the
           panel's own inbox, each with a mono tally on the right. The list IS the
           feeling the section is arguing for. -->
      <ul class="mt-4 overflow-hidden rounded-box border hair bg-base-100">
        {#each [
          ['DMs you haven’t answered.', '4'],
          ['New members who joined and vanished.', '11'],
          ['Questions buried three posts deep.', '7'],
          ['Members who haven’t engaged in weeks.', '38'],
          ['Comments you meant to reply to Tuesday.', '9'],
          ['People quietly drifting toward the cancel button.', '?'],
        ] as [line, tally] (line)}
          <li class="flex items-center gap-4 border-b hair px-4 py-3 last:border-b-0">
            <span class="h-1.5 w-1.5 shrink-0 rounded-full bg-error/70"></span>
            <span class="min-w-0 flex-1 text-[0.95rem] font-medium">{line}</span>
            <span class="font-mono text-xs tabular-nums text-base-content/45">{tally}</span>
          </li>
        {/each}
      </ul>

      <div class="mt-9 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>And you?</p>
        <p>You’re clicking around Skool trying to figure out…</p>
      </div>
      <p class="mt-5 font-display text-2xl font-extrabold leading-tight tracking-tight sm:text-3xl">
        “Who the hell actually needs my attention today?”
      </p>
    </div>
  </div>
</section>

<!-- ── The big idea · THE SIGNATURE ─────────────────────────────────────────── -->
<section class="border-b hair bg-base-200/40">
  <div class="mx-auto max-w-5xl px-6 py-20 lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
    <p class="eyebrow mb-6 text-base-content/55 lg:mb-0 lg:pt-3">The big idea</p>
    <div class="max-w-[38rem]">
      <h2 class="font-display text-3xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-[2.75rem]">
        More members won’t fix this.
      </h2>

      <div class="mt-7 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>Pouring more traffic into a community that’s leaking members is like…</p>
        <p class="font-display text-xl font-bold tracking-tight text-base-content">
          Filling a bucket with a hole in the bottom.
        </p>
      </div>

      <div class="mt-7 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>Sure.</p>
        <p>You can run more ads.</p>
        <p>Post more content.</p>
        <p>Do another collab.</p>
        <p>Climb Discovery.</p>
        <p>Bring another 50 people through the front door.</p>
      </div>

      <div class="mt-7 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>But if members aren’t getting activated…</p>
        <p>If good questions disappear…</p>
        <p>If engagement becomes concentrated around the same handful of people…</p>
        <p>If quiet members go unnoticed until Stripe tells you they’ve cancelled…</p>
        <p class="pt-2 font-display text-xl font-bold tracking-tight text-base-content">
          You’re winning customers you should never have needed to win twice.
        </p>
      </div>

      <p class="mt-7 text-base leading-relaxed text-base-content/75">
        That’s the game most community owners miss. Growth isn’t just
        <span class="font-semibold text-base-content">get more members</span>. It’s:
      </p>

      <!--
        `#leak` — THE ONE MEMORABLE OBJECT ON THE PAGE.

        The copy's whole argument is a bucket with a hole in it, and this is that
        sentence drawn in the product's own handwriting: four stage pills on a rule,
        which is exactly how `PipelineBoard` and `MomentumStages` render a member
        lifecycle inside the panel. Members fall out of the JOINS between stages,
        because that is where they actually go — nobody churns "at" a stage, they churn
        on the way from one to the next.

        WHY THIS AND NOT A HERO ANIMATION. It sits here, in the only section that argues
        rather than describes, so the motion is doing a job: the eye follows a dot
        downward and out, and the next line of copy says what that costs. In the hero it
        would have been atmosphere.

        The drips are `::before`/`::after` on each join — two per gap, offset in time so
        the fall never looks metronomic — and the whole thing is CSS. Under
        `prefers-reduced-motion` the dots stop and sit at the top of their fall, so the
        diagram still reads as "these are the gaps" without anything moving.
      -->
      <figure id="leak" class="mt-8">
        <div class="rounded-box border hair bg-base-100 px-4 pb-14 pt-7 sm:px-7">
          <!-- Stage labels. -->
          <div class="flex items-stretch">
            {#each funnel as st, i (st.key)}
              <div class="flex min-w-0 flex-1 flex-col items-center px-1 text-center">
                <span class="font-display text-sm font-bold tracking-tight text-primary sm:text-base">{st.label}</span>
                <!-- privacy-ok: a hardcoded funnel caption on the PUBLIC landing page
                     ('They join', 'They post') — copy, not anybody's data -->
                <span class="mt-1 min-h-[1.4rem] text-[0.7rem] leading-tight text-base-content/55">{st.note}</span>
              </div>
              {#if i < funnel.length - 1}<div class="w-9 shrink-0 sm:w-14"></div>{/if}
            {/each}
          </div>
          <!-- THE RAIL, and the holes in it. The drips hang off the GAPS in this row —
               not off the labels above — so they fall out of the bottom of the run, into
               the padding below, which is the whole picture: the bucket, and the leak. -->
          <div class="mt-5 flex items-center">
            {#each funnel as st, i (st.key)}
              <div class="h-2.5 flex-1 rounded-full bg-primary/75"></div>
              {#if i < funnel.length - 1}
                <div class="leak-gap relative h-2.5 w-9 shrink-0 sm:w-14" aria-hidden="true"></div>
              {/if}
            {/each}
          </div>
        </div>
        <figcaption class="mt-3 font-mono text-[0.7rem] leading-relaxed text-base-content/55">
          Every gap is a place members leave. Sideguide watches the gaps.
        </figcaption>
      </figure>

      <p class="mt-8 text-base leading-relaxed text-base-content/75">
        Without spending your entire bloody day doing it manually.
      </p>
    </div>
  </div>
</section>

<!-- ── Introducing ──────────────────────────────────────────────────────────── -->
<section class="border-b hair">
  <div class="mx-auto max-w-5xl px-6 py-20 lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
    <p class="eyebrow mb-6 text-base-content/55 lg:mb-0 lg:pt-3">Introducing</p>
    <div class="max-w-[38rem]">
      <h2 class="font-display text-3xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-[2.75rem]">
        Meet Sideguide.
      </h2>
      <p class="mt-4 font-display text-xl font-bold tracking-tight text-base-content/60 sm:text-2xl">
        Your command center for running a growing Skool without becoming its
        full-time babysitter.
      </p>

      <p class="mt-8 text-base leading-relaxed text-base-content/75">
        Sideguide watches what’s happening across your community and turns it into
        something useful:
      </p>

      <!-- THE ANSWER TO THE PROBLEM SECTION'S QUESTION, and drawn as its mirror image:
           the same hairlined stack, but resolved — each row now carries the ACTION and a
           stage pill instead of an unread tally. The two lists are meant to be recognised
           as the same object in two states, which is the entire pitch in one visual.

           No invented member names. A made-up "Marcus W." would be a fabricated customer
           on a page whose whole credibility rests on every other name being real. -->
      <div class="mt-5 overflow-hidden rounded-box border hair bg-base-100">
        <div class="flex items-center justify-between gap-3 border-b hair bg-base-200/60 px-4 py-2.5">
          <span class="eyebrow text-[0.6rem]">Needs you today</span>
          <span class="font-mono text-[0.65rem] tabular-nums text-base-content/50">6 items</span>
        </div>
        {#each [
          ['At risk', 'text-error', '3 paying members went quiet this week'],
          ['Unanswered', 'text-warning', '1 question, open 3 days'],
          ['Newcomer', 'text-info', '4 joined, none posted yet'],
          ['Silent', 'text-base-content/50', '38 members, no activity in 30 days'],
          ['Gap', 'text-warning', '2 topics asked repeatedly, nothing covers them'],
          ['Reply', 'text-primary', '9 comments waiting on you'],
        ] as [pill, tone, line] (line)}
          <div class="flex items-center gap-3 border-b hair px-4 py-3 last:border-b-0">
            <span class="w-[5.5rem] shrink-0 font-mono text-[0.6rem] uppercase tracking-[0.1em] {tone}">{pill}</span>
            <span class="min-w-0 flex-1 text-[0.95rem]">{line}</span>
          </div>
        {/each}
      </div>
      <p class="mt-3 font-display text-lg font-bold tracking-tight">
        A punch-list of what actually deserves your attention.
      </p>

      <div class="mt-8 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>Instead of opening Skool and hunting around…</p>
        <p>You can see where members are losing momentum.</p>
        <p>Who needs reactivating.</p>
        <p>Where participation is dropping.</p>
        <p>Which questions need attention.</p>
        <p>What content is creating value.</p>
        <p>And what needs doing next.</p>
      </div>

      <div class="mt-7 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>So instead of spending Tuesday clearing notifications…</p>
        <p class="font-display text-xl font-bold tracking-tight text-base-content">
          You spend Tuesday growing the business.
        </p>
      </div>

      <div class="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <button class="btn btn-primary gap-2" onclick={getStarted}>
          Get Sideguide free
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
        <p class="eyebrow text-base-content/60">Free for communities under {FREE_MEMBER_CAP} members</p>
      </div>
    </div>
  </div>
</section>

<!-- ── Benefits ─────────────────────────────────────────────────────────────── -->
<section class="border-b hair bg-base-200/40">
  <div class="mx-auto max-w-5xl px-6 py-20 lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
    <p class="eyebrow mb-6 text-base-content/55 lg:mb-0 lg:pt-3">What it plugs</p>
    <div class="max-w-[38rem]">
      <h2 class="font-display text-3xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-[2.75rem]">
        Sideguide plugs the leaks you can’t afford to ignore.
      </h2>

      <div class="mt-10 divide-y divide-base-300">
        {#each leaks as l (l.label)}
          <!-- privacy-ok: marketing copy from the `leaks` array in this file — no customer data -->
          <div class="py-7 first:pt-0 last:pb-0">
            <p class="eyebrow text-[0.6rem] text-primary">{l.label}</p>
            <h3 class="mt-2.5 font-display text-xl font-bold leading-snug tracking-tight sm:text-2xl">
              {l.title}
            </h3>
            <p class="mt-3 text-base leading-relaxed text-base-content/75">{l.body}</p>
            <p class="mt-3 font-display text-base font-bold tracking-tight">{l.punch}</p>
          </div>
        {/each}
      </div>
    </div>
  </div>
</section>

<!-- ── Owner proof ──────────────────────────────────────────────────────────── -->
<section id="testimonial" class="border-b hair">
  <div class="mx-auto max-w-5xl px-6 py-20">
    <div class="lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
      <p class="eyebrow mb-6 text-base-content/55 lg:mb-0 lg:pt-3">Proof</p>
      <div class="max-w-[38rem]">
        <h2 class="font-display text-3xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-[2.75rem]">
          But don’t take my word for it.
        </h2>
        <p class="mt-4 font-display text-xl font-bold tracking-tight text-base-content/60 sm:text-2xl">
          Real Skool operators are already using Sideguide.
        </p>
      </div>
    </div>

    <!-- Full width under the spine: three cards need the whole measure, and the copy
         column above them is what says whose they are. -->
    <div class="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {#each reviews as r (r.id)}
        <!-- Pinned white, like the Skool discovery cards this audience already reads —
             the one place on the page that deliberately borrows somebody else's visual
             language, because being recognisable as "a Skool thing" is the point. -->
        <div class="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white font-sans">
          <div class="bg-black">
            {#if playing[r.id]}
              <!-- svelte-ignore a11y_media_has_caption -- spoken review; the copy carries the claim -->
              <video class="aspect-video w-full" src="/media/testimonial-{r.id}.mp4"
                     poster="/media/testimonial-{r.id}-poster.jpg" controls autoplay></video>
            {:else}
              <button type="button" class="group relative block w-full cursor-pointer"
                      onclick={() => (playing[r.id] = true)}
                      aria-label="Play {r.name}’s review — {r.duration}">
                <!-- privacy-ok: a testimonial the owner recorded FOR this public page -->
                <img src="/media/testimonial-{r.id}-poster.jpg"
                     alt="{r.name}, {r.community}, on camera"
                     width="960" height="540" loading="lazy"
                     class="aspect-video w-full object-cover" />
                <!-- Bottom-left so the affordance never covers the speaker's face. -->
                <span class="absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-full bg-black/60 px-3.5 py-2 text-sm font-semibold text-white backdrop-blur-sm transition-colors group-hover:bg-black/75">
                  <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <polygon points="6 4 20 12 6 20 6 4" />
                  </svg>
                  Watch · {r.duration}
                </span>
              </button>
            {/if}
          </div>
          <div class="p-5">
            <!-- privacy-ok: a testimonial the owner gave FOR this public page -->
            <a href={r.profile} target="_blank" rel="nofollow noopener"
               class="font-display text-base font-bold tracking-tight text-gray-900 hover:underline">{r.name}</a>
            <p class="mt-0.5 text-sm text-gray-500">{r.community}</p>
            <p class="mt-auto pt-2.5 font-mono text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-gray-900">{r.stat}</p>
          </div>
        </div>
      {/each}
    </div>

    <div class="mt-10 lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
      <div class="hidden lg:block"></div>
      <div class="max-w-[38rem] space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>These aren’t hypothetical communities sitting in a Figma mockup.</p>
        <p>
          Sideguide is being used across Skool communities ranging from small operators to
          communities with
          <span class="font-semibold text-base-content">{largest} members.</span>
        </p>
      </div>
    </div>
  </div>
</section>

<!-- ── Member proof ─────────────────────────────────────────────────────────── -->
<section class="border-b hair bg-base-200/40">
  <div class="mx-auto max-w-5xl px-6 py-20">
    <div class="lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
      <p class="eyebrow mb-6 text-base-content/55 lg:mb-0 lg:pt-3">Their members</p>
      <div class="max-w-[38rem]">
        <h2 class="font-display text-3xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-[2.75rem]">
          And their members notice the difference too.
        </h2>
      </div>
    </div>

    <div class="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {#each memberQuotes as q (q.id)}
        <figure class="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 font-sans">
          <blockquote class="flex-1 text-[15px] leading-relaxed text-gray-700">“{q.quote}”</blockquote>
          <figcaption class="mt-6 flex items-center gap-3">
            <img src="/media/testimonial-{q.id}-profile.jpg" alt="" width="400" height="400"
                 loading="lazy" class="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-black/5" />
            <span class="min-w-0">
              <!-- privacy-ok: a review this member gave FOR this public page -->
              <a href={q.profile} target="_blank" rel="nofollow noopener"
                 class="block truncate font-bold text-gray-900 hover:underline">{q.name}</a>
              <span class="text-sm text-gray-500">Vibe Consultant Pro</span>
            </span>
          </figcaption>
        </figure>
      {/each}
    </div>
  </div>
</section>

<!-- ── The offer ────────────────────────────────────────────────────────────── -->
<section class="border-b hair">
  <div class="mx-auto max-w-5xl px-6 py-20 lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
    <p class="eyebrow mb-6 text-base-content/55 lg:mb-0 lg:pt-3">The offer</p>
    <div class="max-w-[38rem]">
      <h2 class="font-display text-3xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-[2.75rem]">
        Okay. How much does all this cost?
      </h2>

      <div class="mt-7 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>If you’re under {FREE_MEMBER_CAP} members…</p>
        <p class="font-display text-xl font-bold tracking-tight text-base-content">Nothing.</p>
        <p>Zip.</p>
        <p>Nada.</p>
        <p>The grand total of…</p>
      </div>

      <!-- The price IS the section, so it is set at display scale rather than dropped
           into a card. A pricing box here would make $0 look like one option among
           several, which is the opposite of what the copy is doing. -->
      <p class="mt-6 font-display text-[4.5rem] font-extrabold leading-none tracking-[-0.04em] text-primary sm:text-[6rem]">
        $0<span class="align-top text-2xl font-bold tracking-tight text-base-content/45 sm:text-3xl">/month</span>
      </p>

      <div class="mt-7 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>You get the full Sideguide command center for one community with up to {FREE_MEMBER_CAP} members.</p>
        <p>No crippled “free version.”</p>
        <p>No credit card.</p>
        <p>Use the thing.</p>
        <p>See whether it makes running your Skool easier.</p>
        <p>Then decide.</p>
      </div>

      <button class="btn btn-primary btn-lg mt-8 gap-2" onclick={getStarted}>
        Start free
        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </div>
  </div>
</section>

<!-- ── Pro ──────────────────────────────────────────────────────────────────── -->
<section id="pricing" class="border-b hair bg-base-200/40">
  <div class="mx-auto max-w-5xl px-6 py-20 lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
    <p class="eyebrow mb-6 text-base-content/55 lg:mb-0 lg:pt-3">Pro</p>
    <div class="max-w-[38rem]">
      <h2 class="font-display text-3xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-[2.75rem]">
        Already past {FREE_MEMBER_CAP} members?
      </h2>

      <div class="mt-7 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>Then there’s one plan.</p>
        <p>No pricing Rubik’s cube.</p>
        <p>No “contact sales.”</p>
        <p>No wondering whether the button you actually need is hidden in the Enterprise plan.</p>
      </div>

      <!-- ONE card, not a two-column table. The copy above it spends four lines mocking
           pricing tables, so shipping one underneath would have the page arguing with
           itself. Free already had its own section and its own $0. -->
      <div class="mt-8 rounded-box border border-primary/30 bg-base-100 p-7 sm:p-8">
        <p class="eyebrow text-primary">Sideguide Pro</p>
        <div class="mt-4 flex flex-wrap items-end gap-x-3 gap-y-1">
          <span class="font-display text-2xl font-semibold tracking-tight text-base-content/40 line-through">${PRO_PRICE}</span>
          <span class="font-display text-[3.5rem] font-extrabold leading-none tracking-[-0.03em]">${EARLY_PRICE}</span>
          <span class="mb-2 text-base-content/60">/month</span>
        </div>
        <p class="mt-3 font-display text-base font-bold tracking-tight">Early-bird price locked in forever.</p>

        <p class="mt-6 text-sm text-base-content/70">You get:</p>
        <ul class="mt-3 grid gap-x-6 gap-y-2 text-[0.95rem] text-base-content/80 sm:grid-cols-2">
          {#each ['Momentum & churn detection', 'Retention cohorts', 'Participation insights',
                  'Contributor value-vs-volume map', 'Reactivation queue', 'Questions & gaps punch-list',
                  'Moderation inbox', 'Content controls', 'Locked-lesson upsells', '24/7 AI tutor',
                  'Rated conversation logs', 'And every feature built for Sideguide Pro'] as f (f)}
            <li class="flex gap-2.5">
              <span class="mt-[0.15rem] shrink-0 text-primary" aria-hidden="true">✓</span>
              <span>{f}</span>
            </li>
          {/each}
        </ul>

        <button class="btn btn-primary btn-lg mt-8 w-full gap-2" onclick={getStarted}>
          Start my 30-day trial
        </button>
        <p class="mt-3 text-center font-mono text-[0.7rem] text-base-content/60">
          30-day free trial · No card required · Cancel anytime
        </p>
      </div>
    </div>
  </div>
</section>

<!-- ── Price anchor ─────────────────────────────────────────────────────────── -->
<section class="border-b hair">
  <div class="mx-auto max-w-5xl px-6 py-20 lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
    <p class="eyebrow mb-6 text-base-content/55 lg:mb-0 lg:pt-3">The math</p>
    <div class="max-w-[38rem]">
      <h2 class="font-display text-3xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-[2.75rem]">
        Here’s the stupidly simple math.
      </h2>

      <div class="mt-7 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>If you’re running a paid community…</p>
        <p>
          How much is <span class="font-semibold text-base-content">one member</span> worth
          over six months?
        </p>
        <p>Now multiply that by the people who quietly disappear because they never got activated…</p>
        <p>Stopped engaging…</p>
        <p>Couldn’t find what they needed…</p>
        <p>Or simply fell through the cracks.</p>
      </div>

      <p class="mt-7 text-base text-base-content/75">
        Then add the hours you’re spending every week manually checking:
      </p>

      <!-- The seven chores as mono chips: a run of prose here would read as filler, and
           the copy's actual point is that the LIST does not stop. -->
      <div class="mt-4 flex flex-wrap gap-2">
        {#each ['DMs', 'Posts', 'Comments', 'Questions', 'Members', 'Notifications', 'Activity'] as chore (chore)}
          <span class="rounded-field border hair bg-base-100 px-2.5 py-1 font-mono text-[0.7rem] text-base-content/70">{chore}</span>
        {/each}
      </div>

      <p class="mt-5 text-base leading-relaxed text-base-content/75">
        And all the other digital confetti required to keep the place moving.
      </p>

      <div class="mt-8 border-l-2 border-primary/25 pl-5">
        <p class="font-display text-xl font-bold tracking-tight">Sideguide is ${EARLY_PRICE}/month.</p>
        <div class="mt-3 space-y-1.5 text-base leading-relaxed text-base-content/75">
          <p>It doesn’t need to perform witchcraft.</p>
          <p>It needs to help you retain a few more members…</p>
          <p>Save a few hours…</p>
          <p>Or surface one problem you otherwise would’ve missed.</p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ── Final CTA ────────────────────────────────────────────────────────────── -->
<section class="hero-glow border-b hair">
  <div class="mx-auto max-w-5xl px-6 py-20 lg:grid lg:grid-cols-[7rem_1fr] lg:gap-12">
    <p class="eyebrow mb-6 text-base-content/55 lg:mb-0 lg:pt-3">Last thing</p>
    <div class="max-w-[38rem]">
      <h2 class="font-display text-3xl font-extrabold leading-[1.02] tracking-[-0.025em] sm:text-[3.25rem]">
        Your job is to grow the community.
      </h2>
      <p class="mt-4 font-display text-xl font-bold tracking-tight text-base-content/60 sm:text-2xl">
        Not spend your life maintaining it.
      </p>

      <div class="mt-8 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>You didn’t start a paid Skool because you had a lifelong dream of clearing notifications.</p>
        <p>You built it to create value.</p>
        <p>Build an audience.</p>
        <p>Help members get results.</p>
        <p>And create recurring revenue.</p>
      </div>

      <p class="mt-7 text-base leading-relaxed text-base-content/75">
        So let Sideguide handle more of the operational grunt work… while you get back to
        doing the work that actually grows the damn thing.
      </p>

      <!-- The two doors, as one hairlined pair rather than two cards: the answer depends
           on a single number the reader already knows about themselves. -->
      <dl class="mt-9 grid gap-px overflow-hidden rounded-box border hair bg-base-300 sm:grid-cols-2">
        <div class="bg-base-100 p-5">
          <dt class="eyebrow text-[0.6rem] text-base-content/55">Under {FREE_MEMBER_CAP} members?</dt>
          <dd class="mt-2 font-display text-lg font-bold tracking-tight">Use Sideguide free.</dd>
        </div>
        <div class="bg-base-100 p-5">
          <dt class="eyebrow text-[0.6rem] text-base-content/55">Over {FREE_MEMBER_CAP}?</dt>
          <dd class="mt-2 font-display text-lg font-bold tracking-tight">Try everything free for 30 days.</dd>
        </div>
      </dl>

      <div class="mt-7 space-y-1.5 text-base leading-relaxed text-base-content/75">
        <p>No card.</p>
        <p>No giant implementation.</p>
        <p>No marriage proposal on the first date.</p>
        <p>Just plug it in and see what you’ve been missing.</p>
      </div>

      <button class="btn btn-primary btn-lg mt-9 gap-2" onclick={getStarted}>
        Get Sideguide
        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </div>
  </div>
</section>

<!-- ── Footer ───────────────────────────────────────────────────────────────── -->
<footer>
  <div class="mx-auto max-w-5xl px-6 py-10">
    <p class="font-display text-lg font-bold tracking-tight">Sideguide</p>
    <p class="mt-1 font-mono text-[0.75rem] italic text-base-content/60">Stop working for your Skool.</p>
    <div class="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-base-content/65">
      <button class="link link-hover" onclick={getStarted}>Get started</button>
      <button class="link link-hover" onclick={() => navigate('/admin/login')}>Sign in</button>
      <a class="link link-hover" href="/privacy.html">Privacy</a>
      <span class="ml-auto font-mono text-[0.7rem]">© {new Date().getFullYear()} Sideguide</span>
    </div>
  </div>
</footer>

<style>
  /* THE LEAK. Two drips per join, on one keyframe, offset so the pair never falls in
     step — a metronome reads as a loading spinner, an uneven drip reads as a leak.
     `::before`/`::after` rather than elements so the markup stays a plain flex row. */
  .leak-gap::before,
  .leak-gap::after {
    content: '';
    position: absolute;
    top: 100%;
    width: 7px;
    height: 7px;
    border-radius: 9999px;
    background: color-mix(in oklch, var(--color-error) 85%, transparent);
    animation: leak-fall 3.4s ease-in infinite;
  }
  .leak-gap::before {
    left: calc(50% - 8px);
  }
  .leak-gap::after {
    left: calc(50% + 3px);
    animation-delay: 1.7s;
    animation-duration: 3.9s;
  }

  @keyframes leak-fall {
    0% {
      transform: translateY(0);
      opacity: 0;
    }
    12% {
      opacity: 1;
    }
    100% {
      transform: translateY(3rem);
      opacity: 0;
    }
  }

  /* Reduced motion: the drips STAY, holding at the top of the fall. The diagram's job
     is to say "these are the gaps members fall through", and that reads perfectly well
     as two still dots under each join — so the meaning survives with the motion gone. */
  @media (prefers-reduced-motion: reduce) {
    .leak-gap::before,
    .leak-gap::after {
      animation: none;
      opacity: 0.7;
    }
  }
</style>
